import React, {
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  CircularProgress,
  Skeleton,
  Typography,
} from "@mui/material";
import LoginForm from "../components/Auth/LoginForm";
import RegistrationForm from "../components/Auth/RegistrationForm";
import { AuthContext } from "../contexts/AuthContext";
import { useAuthVerification } from "../hooks/useAuthVerification";
import AuthSwitchPrompt from "../components/Auth/utils/AuthSwitchPrompt";

const AUTH_ALLOWED_REQUEST_TYPES = new Set([
  "login",
  "register",
  "verify_admin",
  "verify_subsidiary",
]);
const POST_LOGIN_REDIRECT_FLAG_KEY = "post_login_redirect_pending";
const POST_LOGIN_REDIRECT_URL_KEY = "post_login_redirect_url";

const AuthPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    verifyUser,
    verifySubUser,
    verifySubsidiaryUser,
    global_authState,
    authBootstrapComplete,
    switchAccountForVerification,
  } = useContext(AuthContext);
  const [sessionDecision, setSessionDecision] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [postLoginRedirectPending, setPostLoginRedirectPending] = useState(
    () => {
      try {
        return sessionStorage.getItem(POST_LOGIN_REDIRECT_FLAG_KEY) === "1";
      } catch {
        return false;
      }
    }
  );

  const rawRequestType = searchParams.get("request_type");
  const requestType = useMemo(() => {
    if (!rawRequestType) return "login";
    return AUTH_ALLOWED_REQUEST_TYPES.has(rawRequestType)
      ? rawRequestType
      : "login";
  }, [rawRequestType]);

  const token = searchParams.get("token");
  const invitationToken = searchParams.get("invitation_token");
  const subsidiaryToken = searchParams.get("subsidiary_token");

  const requiresInvitation = useMemo(() => {
    return requestType === "register" && !!invitationToken;
  }, [requestType, invitationToken]);
  const isVerificationRequest = useMemo(() => {
    return (
      requestType === "verify_admin" ||
      requestType === "verify_subsidiary" ||
      requiresInvitation
    );
  }, [requestType, requiresInvitation]);
  const isAuthMode = useMemo(
    () => requestType === "login" || requestType === "register",
    [requestType]
  );
  const isLoggedIn = Boolean(global_authState?.uid);
  const signedInEmail =
    global_authState?.email || global_authState?.user_email || "";
  const needsSessionDecision =
    isLoggedIn &&
    (isVerificationRequest || isAuthMode) &&
    !postLoginRedirectPending;
  const canRunVerificationCheck =
    !isVerificationRequest || authBootstrapComplete;
  const verificationEnabled =
    canRunVerificationCheck &&
    (!needsSessionDecision || sessionDecision === "switch");

  const scrubSensitiveParamsFromUrl = useCallback(
    ({ nextRequestType, dropInvitationTokenOnly }) => {
      const next = new URLSearchParams(searchParams);
      if (dropInvitationTokenOnly) {
        next.delete("invitation_token");
      } else {
        next.delete("token");
        next.delete("invitation_token");
        next.delete("subsidiary_token");
      }
      if (nextRequestType != null) {
        next.set("request_type", nextRequestType);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const {
    message,
    setMessage,
    verificationStatus,
    verificationLoading,
    userDetails,
    invitationLoading,
    invitationVerified,
    invitationVerificationFailed,
    effectiveInvitationToken,
    clearAuthFeedback,
  } = useAuthVerification({
    enabled: verificationEnabled,
    requestType,
    token,
    invitationToken,
    subsidiaryToken,
    requiresInvitation,
    verifyUser,
    verifySubUser,
    verifySubsidiaryUser,
    scrubSensitiveParamsFromUrl,
  });

  useEffect(() => {
    if (!needsSessionDecision) {
      setSessionDecision(null);
      setDecisionLoading(false);
    }
  }, [needsSessionDecision]);

  useEffect(() => {
    if (!postLoginRedirectPending || !isLoggedIn) return;
    let redirectTarget = "/consolePanel";
    try {
      const storedRedirectUrl = sessionStorage.getItem(
        POST_LOGIN_REDIRECT_URL_KEY
      );
      if (
        storedRedirectUrl &&
        storedRedirectUrl.startsWith("/") &&
        !storedRedirectUrl.startsWith("/auth")
      ) {
        redirectTarget = storedRedirectUrl;
      }
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_URL_KEY);
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_FLAG_KEY);
    } catch {
      /* ignore */
    }
    setPostLoginRedirectPending(false);
    window.location.replace(redirectTarget);
  }, [postLoginRedirectPending, isLoggedIn]);

  useEffect(() => {
    if (rawRequestType && !AUTH_ALLOWED_REQUEST_TYPES.has(rawRequestType)) {
      setSearchParams(new URLSearchParams({ request_type: "login" }), {
        replace: true,
      });
    }
  }, [rawRequestType, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("request_type")) return;

    const params = new URLSearchParams(searchParams);
    params.set("request_type", "login");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const toggleForm = () => {
    clearAuthFeedback();
    const nextType = requestType === "login" ? "register" : "login";
    const params = new URLSearchParams(searchParams);
    params.set("request_type", nextType);
    setSearchParams(params, { replace: true });
  };

  const goToLoginFromInvitationError = useCallback(() => {
    clearAuthFeedback();
    setSearchParams(new URLSearchParams({ request_type: "login" }), {
      replace: true,
    });
  }, [clearAuthFeedback, setSearchParams]);

  const canRenderRegistrationForm =
    requestType === "register" &&
    (!effectiveInvitationToken || (invitationVerified && !!userDetails.email));

  const handleStayLoggedIn = useCallback(() => {
    setSessionDecision("stay");
    clearAuthFeedback();
    scrubSensitiveParamsFromUrl({ nextRequestType: "login" });
    window.location.replace("/consolePanel");
  }, [clearAuthFeedback, scrubSensitiveParamsFromUrl]);

  const handleSwitchAccount = useCallback(async () => {
    setDecisionLoading(true);
    clearAuthFeedback();
    try {
      await switchAccountForVerification?.();
      setSessionDecision("switch");
    } finally {
      setDecisionLoading(false);
    }
  }, [clearAuthFeedback, switchAccountForVerification]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        flex: "1 1 0%",
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        scrollPaddingBlock: 24,
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
        backgroundColor: "#ffffff",
      }}
    >
      <Box sx={{ width: "100%", my: "auto", flexShrink: 0 }}>
        {message && (
          <Alert
            severity={verificationStatus === "error" ? "error" : "info"}
            sx={{
              mx: "auto",
              mb: 2,
              maxWidth: 500,
              width: "90%",
              borderRadius: 2,
              boxShadow: 3,
              zIndex: 1000,
              fontWeight: 500,
              backgroundColor:
                verificationStatus === "error" ? "#f8d7da" : "#e6f0ff",
              color: verificationStatus === "error" ? "#721c24" : "#004085",
              borderColor:
                verificationStatus === "error" ? "#f5c6cb" : "#b8daff",
            }}
          >
            {message}
          </Alert>
        )}

        <Box
          sx={{
            width: "100%",
            maxWidth: requestType === "register" ? 720 : 450,
            mx: "auto",
            mb: userDetails.email ? 0 : isAuthMode ? 0 : 3,
          }}
        >
          {isVerificationRequest && !authBootstrapComplete ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={32} sx={{ mb: 2 }} />
              <Typography variant='body2' color='text.secondary'>
                Checking your current session...
              </Typography>
            </Box>
          ) : needsSessionDecision && sessionDecision !== "switch" ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Alert severity='info' sx={{ mb: 2, textAlign: "left" }}>
                You are already signed in.
              </Alert>
              {signedInEmail ? (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 1, fontStyle: "italic" }}
                >
                  Signed in as: {signedInEmail}
                </Typography>
              ) : null}
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                {isVerificationRequest
                  ? "Switch account (log out first) to continue verification, or stay signed in and skip this link."
                  : "If you want to use a different account, switch account first. Otherwise stay signed in to continue with your current account."}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant='contained'
                  onClick={handleSwitchAccount}
                  disabled={decisionLoading}
                >
                  {decisionLoading
                    ? "Switching..."
                    : "Switch account & continue"}
                </Button>
                <Button
                  variant='outlined'
                  onClick={handleStayLoggedIn}
                  disabled={decisionLoading}
                >
                  Stay signed in
                </Button>
              </Box>
            </Box>
          ) : verificationLoading &&
            (requestType === "verify_admin" ||
              requestType === "verify_subsidiary") ? (
            <Box
              sx={{
                textAlign: "center",
                py: 6, // 增加上下间距，提升呼吸感
                px: 4,
                mx: "auto",
                maxWidth: 360, // 限制最大宽度，保持视觉紧凑
                borderRadius: 3, // 增加圆角
                backgroundColor: "rgba(0, 0, 0, 0.02)", // 极浅的背景色，形成微妙的区块感（暗色模式下可调整为 rgba(255,255,255,0.05)）
                border: "1px solid",
                borderColor: "divider", // 使用主题自带的分割线颜色，完美适配明暗模式
              }}
              aria-busy='true'
              role='status' // 增强无障碍访问支持
            >
              {/* 带有脉冲/呼吸效果的 Loading 图标 */}
              <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
                <CircularProgress
                  size={40}
                  thickness={4}
                  sx={{
                    color: "primary.main",
                    animationDuration: "800ms", // 稍微加快旋转速度，显得更积极
                  }}
                />
                {/* 背景光晕效果，增强科技感 */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    opacity: 0.2,
                    filter: "blur(8px)",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              </Box>

              {/* 优化的文案排版 */}
              <Typography
                variant='body1' // 稍微放大字号
                sx={{
                  fontWeight: 500,
                  color: "text.primary", // 使用主文本色，提高对比度
                  letterSpacing: "0.5px", // 增加字间距，提升精致感
                }}
              >
                {requestType === "verify_subsidiary"
                  ? "Sending your 2FA email..."
                  : "Verifying your email..."}
              </Typography>

              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mt: 1, display: "block" }}
              >
                Please wait a moment...
              </Typography>
            </Box>
          ) : requestType === "register" ? (
            effectiveInvitationToken ? (
              invitationLoading ? (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 450,
                    py: 1,
                  }}
                  aria-busy='true'
                  aria-label='Verifying invitation'
                >
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    Verifying your invitation...
                  </Typography>
                  <Skeleton
                    variant='rounded'
                    height={56}
                    sx={{ mb: 2, borderRadius: 1 }}
                  />
                  <Skeleton
                    variant='rounded'
                    height={56}
                    sx={{ mb: 2, borderRadius: 1 }}
                  />
                  <Skeleton
                    variant='rounded'
                    height={56}
                    sx={{ mb: 2, borderRadius: 1 }}
                  />
                  <Skeleton
                    variant='rounded'
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant='body2' color='text.secondary'>
                      Please wait
                    </Typography>
                  </Box>
                </Box>
              ) : canRenderRegistrationForm ? (
                <RegistrationForm
                  invitationToken={effectiveInvitationToken}
                  setMessage={setMessage}
                  {...userDetails}
                />
              ) : invitationVerificationFailed ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    px: 2,
                    maxWidth: 450,
                    width: "100%",
                  }}
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 2 }}
                  >
                    Check the link for typos, or ask your administrator for a
                    new invitation.
                  </Typography>
                  <Button
                    variant='contained'
                    onClick={goToLoginFromInvitationError}
                  >
                    Return to sign in
                  </Button>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={32} sx={{ mb: 2 }} />
                  <Typography variant='body2' color='text.secondary'>
                    Preparing your registration...
                  </Typography>
                </Box>
              )
            ) : (
              // ✅ normal register (no invitation token)
              <RegistrationForm setMessage={setMessage} {...userDetails} />
            )
          ) : (
            <LoginForm setMessage={setMessage} />
          )}
        </Box>

        {isAuthMode && (
          <AuthSwitchPrompt requestType={requestType} onToggle={toggleForm} />
        )}
      </Box>
    </Box>
  );
};

export default AuthPage;
